/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import {
  useGetCustomerBoIAM,
  CustomerBoIAM
} from '@utils/api/back-office/customer-bo/use-get-customer-bo-iam.tsx';
import { Params, useParams } from 'react-router-dom';
import Input from '@components/atoms/ui-kit/input.tsx';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useGetCustomerIamGroup } from '@utils/api/back-office/customer-bo/use-get-group-bo.tsx';
import {
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue
} from 'react-hook-form';
import { CreateGroupBoPayload } from '@utils/api/back-office/customer-bo/use-create-group.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { useDeleteGroup } from '@utils/api/back-office/customer-bo/use-delete-group-i-am.tsx';
import { useUpdateGroup } from '@utils/api/back-office/customer-bo/use-update-group.tsx';
import { SelectedGroup } from '@components/back-office/customer/customer-bo-iam-groups.tsx';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';

type Props = {
  selectedGroup: SelectedGroup;
  handleAddMemberToUpdateTeam(user: CustomerBoIAM): void;
  handleRemoveMemberFromUpdateTeam(id: string): void;
  setValue: UseFormSetValue<CreateGroupBoPayload>;
  register: UseFormRegister<CreateGroupBoPayload>;
  errors: FieldErrors<CreateGroupBoPayload>;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
  isEditModalOpen: boolean;
  teamUsersData: CustomerBoIAM[];
  handleSubmit: UseFormHandleSubmit<CreateGroupBoPayload, CreateGroupBoPayload>;
};

export default function IamGroupDetails({
  selectedGroup,
  handleRemoveMemberFromUpdateTeam,
  handleAddMemberToUpdateTeam,
  errors,
  register,
  setValue,
  isEditModalOpen,
  setIsEditModalOpen,
  teamUsersData,
  handleSubmit
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { customerId } = useParams<Params>();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [query, setQuery] = useState('');

  const { data: user } = useGetCustomerBoIAM(Number(customerId));
  const { data: group } = useGetCustomerIamGroup(
    Number(customerId),
    Number(selectedGroup?.id)
  );
  const { mutate: deleteGroup } = useDeleteGroup();
  const updateGroupMutation = useUpdateGroup();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainerColor: { backgroundColor: getColour('appSoft') },
        mainText: { color: getColour('normalText') },
        border: { borderColor: getColour('hairline') }
      }),
    [getColour]
  );

  useEffect(() => {
    if (!group?.result?.name) return;
    setValue('name', group.result.name);
    setValue('description', group.result.description);
    setValue('member_ids', group.result.user_ids);
  }, [group, setValue]);

  const groupUserIds = useMemo(
    () => group?.result?.user_ids ?? [],
    [group?.result?.user_ids]
  );

  useEffect(() => {
    if (!user?.result || groupUserIds.length === 0) return;
    if (teamUsersData.length > 0) return; // parent already has state

    const inGroup = new Set(groupUserIds);
    user.result.forEach(u => {
      if (inGroup.has(u.id)) {
        handleAddMemberToUpdateTeam(u);
      }
    });
  }, [user?.result, groupUserIds]);

  const handleDeleteGroup = () => {
    if (selectedGroup && selectedGroup.id && Number(customerId)) {
      setIsDeleting(true);
      deleteGroup(
        { customerId: Number(customerId), groupId: Number(selectedGroup.id) },
        {
          onSuccess: () => {
            toast.success(t('group-deleted-successfully'));
            setIsEditModalOpen(false);
            setIsDeleting(false);
          },
          onError: (error: AxiosError<BaseError>) => {
            const errorMessage =
              error.response?.data?.error_description ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error.response?.data as any)?.detail ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error.response?.data as any)?.error ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error.response?.data as any)?.message ||
              t('failed-to-delete-the-group');
            toast.error(errorMessage);
            setIsDeleting(false);
          }
        }
      );
    }
  };

  const onUpdateSubmit = async (data: CreateGroupBoPayload) => {
    try {
      setIsSubmitting(true);

      // Send current selection (ids) to API
      await updateGroupMutation.mutateAsync({
        customerId: Number(customerId),
        groupId: Number(selectedGroup?.id),
        data: {
          name: data.name,
          description: data.description,
          user_ids: teamUsersData.map(u => u.id)
        }
      });

      toast.success(t('group-updated-successfully'));
      setIsEditModalOpen(false);
    } catch (error) {
      const axiosError = error as AxiosError<BaseError>;
      const message =
        axiosError?.response?.data?.error_description ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (axiosError?.response?.data as any)?.detail ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (axiosError?.response?.data as any)?.error ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (axiosError?.response?.data as any)?.message ||
        t('failed-to-update-the-group');

      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const FORM_ID = 'iam-group-details-form';

  const allUsers = user?.result ?? [];

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(u => {
      const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      return full.includes(q) || email.includes(q);
    });
  }, [allUsers, query]);

  const selectedIds = useMemo(() => {
    if (teamUsersData && teamUsersData.length > 0) {
      return new Set(teamUsersData.map(u => u.id));
    }
    return new Set(groupUserIds);
  }, [teamUsersData, groupUserIds]);

  const selectedCount = selectedIds.size;

  const toggleUser = (checked: boolean, userItem: CustomerBoIAM) => {
    if (checked) handleAddMemberToUpdateTeam(userItem);
    else handleRemoveMemberFromUpdateTeam(userItem.id.toString());
  };

  const selectAllFiltered = () => {
    filteredUsers.forEach(u => {
      if (!selectedIds.has(u.id)) handleAddMemberToUpdateTeam(u);
    });
  };

  const clearAllFiltered = () => {
    filteredUsers.forEach(u => {
      if (selectedIds.has(u.id))
        handleRemoveMemberFromUpdateTeam(u.id.toString());
    });
  };

  return (
    <Modal
      isOpen={isEditModalOpen}
      onClose={() => setIsEditModalOpen(false)}
      size="large"
      title={t(selectedGroup.name)}
      footerActions={[
        <Button
          onClick={handleDeleteGroup}
          label={isDeleting ? t('deleting') : t('delete')}
          size="small"
          variant="danger"
          key="delete"
          disabled={isSubmitting || isDeleting}
        />,
        <Button
          label={isSubmitting ? t('updating') : t('update')}
          key="update"
          type="submit"
          form={FORM_ID}
          variant="primary"
          size="small"
          disabled={isSubmitting || isDeleting}
        />
      ]}
    >
      <div className="text-left">
        <form
          id={FORM_ID}
          className={cn(
            'flex flex-col text-left',
            'max-h-[70vh] sm:max-h-[65vh]',
            css(styles.mainContainerColor, styles.mainText)
          )}
          onSubmit={handleSubmit(onUpdateSubmit)}
        >
          <button type="submit" className="hidden" aria-hidden="true" />

          {/* Basic info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              id="name"
              label={t('name')}
              defaultValue={group?.result?.name || ''}
              type="text"
              autoComplete="name"
              {...register('name')}
              className="w-full"
              error={errors.name?.message}
            />
            <Input
              id="description"
              label={t('description')}
              defaultValue={group?.result?.description || ''}
              type="text"
              autoComplete="description"
              {...register('description')}
              className="w-full"
              error={errors.description?.message}
            />
          </div>

          {/* Members */}
          <fieldset className="mt-6 flex-1 min-h-0">
            <legend className="block text-sm font-medium mb-2">
              {t('members')}
            </legend>

            {/* Controls */}
            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="w-full sm:w-auto flex-1">
                  <Input
                    id="member-search"
                    placeholder={t('search-by-name-or-email')}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    aria-label={t('search')}
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="xs"
                    variant="light"
                    onClick={selectAllFiltered}
                    label={t('select-all')}
                  />
                  <Button
                    size="xs"
                    variant="smoke"
                    onClick={clearAllFiltered}
                    label={t('clear')}
                  />
                  <span className="text-xs font-medium opacity-80 whitespace-nowrap">
                    {selectedCount} {t('selected')}
                  </span>
                </div>
              </div>
            </div>

            {/* List */}
            <div
              className="min-h-0 overflow-auto pr-1 rounded-2xl"
              style={{
                maxHeight: '42vh'
              }}
            >
              {filteredUsers.length === 0 ? (
                <div className="py-12 px-3 text-sm text-center opacity-60">
                  {query
                    ? t('no-users-match-your-search')
                    : t('no-users-available')}
                </div>
              ) : (
                <div className="grid gap-2.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {filteredUsers.map((userItem: CustomerBoIAM) => {
                    const checked = selectedIds.has(userItem.id);
                    return (
                      <div
                        key={userItem.id}
                        className="p-3 rounded-2xl border border-transparent hover:border-current transition-all duration-200 shadow-sm hover:shadow"
                        style={{
                          backgroundColor: getColour('thirdLayer')
                        }}
                      >
                        <Checkbox
                          id={`member_${userItem.id}`}
                          label={
                            <span className="block truncate">
                              {userItem.first_name} {userItem.last_name}
                              {userItem.email ? (
                                <span className="block text-xs opacity-70 truncate">
                                  {userItem.email}
                                </span>
                              ) : null}
                            </span>
                          }
                          checked={checked}
                          onChange={e => toggleUser(e.target.checked, userItem)}
                          variant="primary"
                          wrapperClass="flex items-center gap-3"
                          labelClass="truncate"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </fieldset>
        </form>
      </div>
    </Modal>
  );
}
